<template>
  <form @submit.prevent="saveForm()">
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head flex justify-between">
        <p class="modal-card-title">
          {{ question ? 'Edit' : 'Add' }} Exam Question
        </p>
      </header>
      <section class="modal-card-body space-y-6">
        <d-field label="Question Text">
          <d-input v-model="form.content" />
        </d-field>

        <d-field label="Question Type" class="w-full">
            <d-select v-model="form.type" required>
                <option value="Multiple Choice">
                    Multiple Choice
                </option>
                <option value="Text">
                    Text Response
                </option>
                <option value="Drop Down">
                    Drop Down
                </option>
                <option value="Multiple Answer">
                    Multiple Answer
                </option>
                <option value="File Upload">
                    File Upload
                </option>
                <option value="True/False">
                    True/False
                </option>
            </d-select>
        </d-field>

        <!-- Question Type Instructions -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p v-if="form.type =='Multiple Answer'" class="text-sm text-blue-800">
                Add as many choices as desired, and select multiple correct answer choices. De-select a choice to remove it.
            </p>
            <p v-if="form.type =='Drop Down' || form.type =='Multiple Choice'" class="text-sm text-blue-800">
                Add as many choices as desired, and select one correct answer choice
            </p>
            <p v-if="form.type =='File Upload'" class="text-sm text-blue-800">
                 Users will be able to upload files as their answer
            </p>
            <p v-if="form.type =='Text'" class="text-sm text-blue-800">
                 Users will be able to supply free text as their answer
            </p>
            <p v-if="form.type =='True/False'" class="text-sm text-blue-800">
                 Users will select True or False
            </p>
        </div>
        <!-- Answer Choices Section -->
        <div v-if="form.type == 'Multiple Choice' || form.type == 'Drop Down' || form.type == 'Multiple Answer'" class="space-y-4">
          <label class="block text-sm font-medium text-gray-700">Answer Choices</label>
          <div 
            v-for="(choice, index) in form.answerChoices" 
            :key="index"
            class="flex items-center space-x-3 p-3 bg-gray-50 rounded-md"
          >
            <d-input 
              v-model="choice.content" 
              placeholder="Enter answer choice"
              class="flex-1"
            />
            <input 
              v-if="form.type === 'Multiple Answer'"
              type="checkbox"
              v-model="choice.isCorrect"
              class="rounded"
            />
            <input 
              v-else
              type="radio"
              :name="'correct-answer'"
              :value="index"
              @change="setCorrectAnswer(index)"
              :checked="choice.isCorrect"
              class="rounded"
            />
            <d-btn 
              size="sm" 
              color="error" 
              @click="removeChoice(index)"
              :disabled="form.answerChoices.length <= 1"
            >
              <d-icon icon="trash" />
            </d-btn>
          </div>
          <div>
            <d-btn size="sm" color="secondary" @click="addChoice()">
              <d-icon icon="plus" class="mr-1" />
              Add Choice
            </d-btn>
          </div>
        </div>

        <!-- True/False Answer Section -->
        <div v-if="form.type == 'True/False'" class="space-y-4">
          <label class="block text-sm font-medium text-gray-700">Correct Answer</label>
          <div class="flex space-x-4">
            <label class="flex items-center">
              <input 
                type="radio" 
                value="true" 
                v-model="correctTrueFalseAnswer"
                class="mr-2"
              />
              True
            </label>
            <label class="flex items-center">
              <input 
                type="radio" 
                value="false" 
                v-model="correctTrueFalseAnswer"
                class="mr-2"
              />
              False
            </label>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot justify-end space-x-3 pt-6 border-t border-gray-200">
        <d-btn flat @click="$emit('close')">
          Cancel
        </d-btn>
        <d-btn :loading="saving" type="submit" color="primary">
          {{ question ? 'Save Changes' : 'Add Question' }}
        </d-btn>
      </footer>
    </div>
  </form>
</template>

<script>
  import axios from 'axios';
  import { showErrorToast } from '@services/errorHandling.service';
  import { toast } from '@services/toast.service';

  export default {
    emits: ['close', 'saved'],
    
    props: {
      exam: {
        type: Object,
        default: () => ({}),
      },
      question: {
        type: Object,
        default: null,
      },
    },

    data() {
      return {
        saving: false,
        form: {
          examId: this.exam.id,
          content: '',
          type: 'Multiple Choice',
          sortIndex: 0,
          answerChoices: [{
            content: '',
            sortIndex: 0,
            isCorrect: false,
          }],
        },
        emptyChoice: {
          content: '',
          sortIndex: 0,
          isCorrect: false,
        },
        correctTrueFalseAnswer: 'true',
      };
    },

    computed: {
      needsAnswerChoices() {
        return ['Multiple Choice', 'Drop Down', 'Multiple Answer'].includes(this.form.type);
      }
    },

    watch: {
      'form.type'() {
        this.handleTypeChange();
      },
      correctTrueFalseAnswer() {
        this.updateTrueFalseAnswers();
      }
    },

    mounted() {
      if (this.question) {
        this.form = JSON.parse(JSON.stringify(this.question));
        this.form.examId = this.exam.id;
        
        // Handle True/False questions
        if (this.form.type === 'True/False' && this.form.answerChoices?.length > 0) {
          const correctChoice = this.form.answerChoices.find(choice => choice.isCorrect);
          this.correctTrueFalseAnswer = correctChoice?.content?.toLowerCase() === 'true' ? 'true' : 'false';
        }
      }
    },

    methods: {
      addChoice() {
        const copiedChoice = JSON.parse(JSON.stringify(this.emptyChoice));
        copiedChoice.sortIndex = this.form.answerChoices.length;
        this.form.answerChoices.push(copiedChoice);
      },

      removeChoice(index) {
        if (this.form.answerChoices.length > 1) {
          this.form.answerChoices.splice(index, 1);
          // Update sort indices
          this.form.answerChoices.forEach((choice, idx) => {
            choice.sortIndex = idx;
          });
        }
      },

      setCorrectAnswer(index) {
        // For single-answer questions, only one can be correct
        this.form.answerChoices.forEach((choice, idx) => {
          choice.isCorrect = idx === index;
        });
      },

      handleTypeChange() {
        if (this.form.type === 'True/False') {
          // Set up True/False choices
          this.form.answerChoices = [
            { content: 'True', sortIndex: 0, isCorrect: this.correctTrueFalseAnswer === 'true' },
            { content: 'False', sortIndex: 1, isCorrect: this.correctTrueFalseAnswer === 'false' }
          ];
        } else if (this.needsAnswerChoices && this.form.answerChoices.length === 0) {
          // Reset to default answer choices for other types
          this.form.answerChoices = [{
            content: '',
            sortIndex: 0,
            isCorrect: false,
          }];
        } else if (!this.needsAnswerChoices) {
          // Clear answer choices for types that don't need them
          this.form.answerChoices = [];
        }
      },

      updateTrueFalseAnswers() {
        if (this.form.type === 'True/False') {
          this.form.answerChoices = [
            { content: 'True', sortIndex: 0, isCorrect: this.correctTrueFalseAnswer === 'true' },
            { content: 'False', sortIndex: 1, isCorrect: this.correctTrueFalseAnswer === 'false' }
          ];
        }
      },

      saveForm() {
        // Validate the form before saving
        if (!this.validateForm()) {
          return;
        }
        this.createQuestion();
      },

      validateForm() {
        // Basic validation for question content
        if (!this.form.content || this.form.content.trim() === '') {
          toast.error('Please enter a question');
          return false;
        }

        // Validation for multiple choice, dropdown, and multiple answer questions
        if (this.needsAnswerChoices) {
          // Check if there are answer choices
          if (!this.form.answerChoices || this.form.answerChoices.length === 0) {
            toast.error('Please add at least one answer choice');
            return false;
          }

          // Check if at least one answer choice has content
          const hasValidChoices = this.form.answerChoices.some(choice => choice.content && choice.content.trim() !== '');
          if (!hasValidChoices) {
            toast.error('Please provide content for at least one answer choice');
            return false;
          }

          // Check if at least one correct answer is selected
          const hasCorrectAnswer = this.form.answerChoices.some(choice => choice.isCorrect);
          if (!hasCorrectAnswer) {
            toast.error('Please select at least one correct answer');
            return false;
          }

          // Check if Multiple Answer questions have at least two correct answers
          if (this.form.type === 'Multiple Answer') {
            const correctAnswerCount = this.form.answerChoices.filter(choice => choice.isCorrect).length;
            if (correctAnswerCount < 2) {
              toast.error('Please select at least two answers for a multiple selection question. If you only want to have one right answer, use the multiple choice question type.');
              return false;
            }
          }
        }

        return true;
      },

      async createQuestion() {
        this.saving = true;
        try {
          if (this.question && this.question.id) {
            await axios.patch(`/api/content-listings/items/interactive/questions/${this.question.id}`, this.form);
          } else {
            await axios.post(`/api/exams/${this.exam.id}/questions`, this.form);
          }
          this.$emit('saved');
          this.$emit('close');
        } catch (error) {
          showErrorToast(error);
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>